import { ISide, IHolder, CanvasBuffer, IDimensions, Await } from "./types"
import { createCanvas, createImage, debounce, pause } from "oj-utils"
import { IDrawTextOptions, getFittedText, drawTextLines, IDrawImageOptions, drawImage } from "oj-canvas-utils"
import * as State from "./state"
import * as ProductConfig from "./products"
import m from "mithril"
import FontLoader from "oj-fontloader"
import placeholder from "../../assets/imgPlaceholder.jpg"

const minSize = 2

interface ICache {
  showPlaceholders: boolean
  side: ISide
  async: boolean
  dirty: boolean
  initialized: boolean,
  printOnly: boolean,
  scale: number,
  stage?: CanvasBuffer,
  assets?: { placeholder: HTMLImageElement },
  config?: Await<ReturnType<ProductConfig.ConfigLoad>>
}

const cache: {
  [k: string]: ICache
} = {}

const getCache = (side: ISide) => {
  const key = `${side.product.key}_${side.key}`
  if (!cache[key])
    cache[key] = {
      side,
      async: true,
      dirty: true,
      scale: 1,
      initialized: false,
      printOnly: false,
      showPlaceholders: true,
    }
  return cache[key]
}

export const load = async (canvas: HTMLCanvasElement, side: ISide) => {
  const c = getCache(side)
  c.stage = { canvas, ctx: canvas.getContext("2d") }
  scaleToCanvasSize(c.stage, side.position, side)

  if (!c.initialized) {
    const [assets, config] = await Promise.all<any>([loadAssets(), loadProductConfig()])
    c.assets = assets
    c.config = config
    c.initialized = true
  }

  const chain = getChain(side)

  window.on(`resize.CanvasRenderer.${side.key}`, debounce(e => {
    scaleToCanvasSize(c.stage, side.position, side)
    chain.render()
  }, 500))

  chain
    .setAsync(false)
    .render()
    .setAsync(true)

  return chain
}

export const renderDebounced = debounce((side: ISide) => {
  render(side)
  m.redraw()
}, 20)

export const render = (side = State.getSide()) => {
  const c = getCache(side)
  const chain = getChain(side)

  if (!c.initialized)
    return chain

  const { canvas, ctx } = c.stage
  const fullRect = { x: 0, y: 0, width: canvas.width, height: canvas.height }
  const config = c.config[side.key] as ICache["config"]["front"]

  ctx.clearRect(0, 0, canvas.width, canvas.height)

  if (!c.printOnly) {
    ctx.save()
    ctx.fillStyle = State.getUrlData().color?.value || "white"
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.restore()

    if (config.assets.texture) {
      ctx.save()
      ctx.globalAlpha = config.assets.textureAlpha || .2
      drawImage(ctx, config.assets.texture, { ...fullRect, fit: "cover" })
      ctx.restore()
    }
  }

  side.holders
    .forEach(x =>
      renderHolderOnState(x, c.stage, c))

  c.dirty = false

  if (!c.printOnly) {

    if (config.assets.mask) {
      ctx.save()
      ctx.globalCompositeOperation = "destination-in"
      drawImage(ctx, config.assets.mask, { ...fullRect, fit: "cover" })
      ctx.restore()
    }
    
    if (config.assets.addition) {
      ctx.save()
      if (config.assets.additionBlend)
        ctx.globalCompositeOperation = config.assets.additionBlend
      ctx.globalAlpha = config.assets.additionAlpha || 1
      drawImage(ctx, config.assets.addition, { ...fullRect, fit: "cover" })
      ctx.restore()
    }
  }

  return chain
}

export const setDirty = (dirty: boolean, side: ISide) => {
  const c = getCache(side)
  c.dirty = dirty
  return getChain(side)
}

export const setAsync = (async: boolean, side: ISide) => {
  const c = getCache(side)
  c.async = async
  return getChain(side)
}

export const setScale = (scale: number = undefined, side: ISide): ReturnType<typeof getChain> => {
  const c = getCache(side)
  if (scale === undefined)
    return scaleToCanvasSize(c.stage, side.position, side)

  c.scale = scale
  const resized = resizeCanvas(c.stage.canvas, side.position, c)
  if (resized)
    setDirty(true, c.side)
  return getChain(side)
}

export const toImage = (type = "image/jpeg", quality = 1, side: ISide) => {
  const c = getCache(side)
  const img = c.stage.canvas.toDataURL(type, quality)
  return img
}

export const setPrintOnly = (printOnly: boolean, side: ISide) => {
  const c = getCache(side)
  c.printOnly = printOnly
  return getChain(side)
}

export const setShowPlaceholders = (show: boolean, side: ISide) => {
  const c = getCache(side)
  c.showPlaceholders = show
  return getChain(side)
}

export const getOptions = (side: ISide) => {
  const { async, scale, printOnly, showPlaceholders } = getCache(side)
  return { async, scale, printOnly, showPlaceholders }
}

const renderHolderOnState = (holder: IHolder, { ctx }: CanvasBuffer, c: ICache) => {
  const r = holder.position.rotation
  let canvas: CanvasImageSource
  let width: number
  let height: number
  let x: number
  let y: number

  if (r % 2 !== 0) {
    const position = { ...holder.position }
    width = scaleUp(position.height, c)
    height = scaleUp(position.width, c)
    x = scaleUp(position.x, c) + (height * .5)
    y = scaleUp(position.y, c) + (width * .5)
    canvas = renderHolder(holder, { width: position.height, height: position.width }, c)
  } else {
    width = scaleUp(holder.position.width, c)
    height = scaleUp(holder.position.height, c)
    x = scaleUp(holder.position.x, c) + (width * .5)
    y = scaleUp(holder.position.y, c) + (height * .5)
    canvas = renderHolder(holder, undefined, c)
  }

  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(Math.PI / 2 * r)
  ctx.drawImage(canvas, -width * .5, -height * .5, width, height)
  ctx.restore()
}

const renderHolder = (holder: IHolder, dimensions: IDimensions = holder.position, c: ICache) => {
  if (!holder.buffer)
    holder.buffer = createCanvas({ width: scaleUp(dimensions.width, c), height: scaleUp(dimensions.height, c) })

  if (c.dirty || holder.dirty) {
    if (c.async)
      renderHolderBufferDebounced(holder, dimensions, c)
    else
      renderHolderBuffer(holder, dimensions, c)
  }

  holder.dirty = false
  return holder.buffer.canvas
}

const renderHolderBufferDebounced = (holder: IHolder, dimensions: IDimensions, c: ICache) => {
  if (!holder.cache.debouncer)
    holder.cache.debouncer = debounce((h: IHolder, d: IDimensions, c: ICache) => {
      renderHolderBuffer(h, d, c)
      renderDebounced(holder.side)
    }, 20)
  holder.cache.debouncer(holder, dimensions, c)
}

const renderHolderBuffer = (holder: IHolder, dimensions: IDimensions, c: ICache) => {
  resizeCanvas(holder.buffer.canvas, dimensions, c)
  if (holder.type === "text")
    renderText(holder, c)
  if (holder.type === "image")
    renderImage(holder, c)
}

const resizeCanvas = (canvas: HTMLCanvasElement, dimensions: IDimensions, c: ICache) => {
  const width = Math.floor(scaleUp(dimensions.width, c))
  const height = Math.floor(scaleUp(dimensions.height, c))
  if (canvas.width === width && canvas.height === height)
    return false
  canvas.width = width
  canvas.height = height
  return true
}

const renderText = (holder: IHolder, c: ICache) => {
  const { ctx, canvas } = holder.buffer
  const rect = { x: 0, y: 0, width: canvas.width, height: canvas.height }

  const getValue = (key: string) => State.getAction(key, holder).value

  let text = getValue("text").trim() as string
  const size = scaleUp(getValue("size"), c)
  const font = getValue("font")
  const textOptions: IDrawTextOptions = {
    ...rect,
    fontSize: size,
    fontSizeMin: scaleUp(minSize, c),
    fontSizeMax: size,
    textWrap: getValue("justify"),
    fillStyle: getValue("color"),
    textAlign: getValue("align"),
    textShrink: true,
    fontFamily: font,
    bold: getValue("style").bold,
    italic: getValue("style").italic,
  }

  ctx.save()
  ctx.clearRect(rect.x, rect.y, rect.width, rect.height)

  if (!text && !c.showPlaceholders)
    return

  ctx.save()
  if (!text) {
    ctx.globalAlpha = .5
    text = "Your text"
  }
  State.setActionState({ min: getValue("size") <= minSize }, holder, "size")
  let { fontSize, lines } = getFittedText(ctx, text, textOptions)
  fontSize = Math.floor(fontSize)
  const min = textOptions.fontSize < textOptions.fontSizeMin + 4
  if (min) {
    fontSize = textOptions.fontSizeMin
    State.setActionValuePassive(scaleDown(textOptions.fontSizeMin, c), holder, "size")
  }
  const max = fontSize < textOptions.fontSizeMax - 4
  if (max) {
    State.setActionValuePassive(scaleDown(fontSize, c), holder, "size")
  }

  State.setActionState({ min, max }, holder, "size")

  drawTextLines(ctx, lines.map(x => " " + x + " "), { ...textOptions, fontSize })
  ctx.restore()
}

const renderImage = (holder: IHolder, c: ICache) => {
  const { ctx, canvas } = holder.buffer
  const rect = { x: 0, y: 0, width: canvas.width, height: canvas.height }
  const image = State.getAction("image", holder).value as HTMLImageElement | "PENDING"
  const imageOptions: IDrawImageOptions = { ...rect, fit: State.getAction("fit", holder).value as any }

  ctx.clearRect(rect.x, rect.y, rect.width, rect.height)

  if (!image && !c.showPlaceholders)
    return

  ctx.save()
  if (!image || image === "PENDING") {
    ctx.globalAlpha = .5
    drawImage(ctx, c.assets.placeholder, { ...rect, fit: "cover" })
  }
  else if (State.getUrlData().embossed) {
    if (holder.cache.image !== image) {
      holder.cache.image = image
      const scale = Math.min(1, Math.min(512 / image.naturalWidth, 512 / image.naturalHeight))
      const inverted = createCanvas({ width: image.naturalWidth * scale, height: image.naturalHeight * scale })
      inverted.ctx.drawImage(image, 0, 0, inverted.canvas.width, inverted.canvas.height)
      holder.cache.inverted = getInvertedTransparency(inverted)

      const colored = createCanvas(inverted.canvas)
      colored.ctx.drawImage(image, 0, 0, colored.canvas.width, colored.canvas.height)
      holder.cache.colored = getColored(colored, "rgba(30, 10, 0, 0.1)")
    }

    drawImage(ctx, holder.cache.colored.canvas, imageOptions)
    ctx.drawImage(getInnerShadow(createCanvas(canvas), holder.cache.inverted.canvas, image, imageOptions, {
      shadowBlur: scaleUp(1.5, c),
      shadowOffsetX: scaleUp(.25, c),
      shadowOffsetY: scaleUp(.5, c)
    }).canvas, 0, 0)
  }
  else
    drawImage(ctx, image, imageOptions)
  ctx.restore()
}

const loadAssets = async (): Promise<{ placeholder: HTMLImageElement }> => {
  const [img] = await Promise.all<any>([
    createImage(placeholder),
    loadFonts()
  ])
  return { placeholder: img }
}

const loadFonts = async () => {
  const fonts = await FontLoader.loadGoogleFonts(ProductConfig.getTextHolderActions().find(x => x.key === "font").options.map(x => x.value) as string[])
  await pause(500)
  return fonts
}

const loadProductConfig = async () => {
  const config = await ProductConfig.loadRenderConfig(State.getUrlData())
  if (config.urlData) {
    State.updateUrlData(config.urlData)
    delete config.urlData
  }
  return config
}

const scaleUp = (n: number, c: ICache) => {
  return n * c.scale
}

const scaleDown = (n: number, c: ICache) => {
  return n / c.scale
}

const scaleToCanvasSize = (b: CanvasBuffer, dimensions: IDimensions, side: ISide) =>
  setScale(Math.max(Math.max(400, b.canvas.clientWidth) / dimensions.width, Math.max(400, b.canvas.clientHeight) / dimensions.height), side)

const getInvertedTransparency = ({ canvas, ctx }: CanvasBuffer) => {
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const pixels = imageData.data
  for (let i = 0; i < pixels.length; i += 4) {
    pixels[i] = 0
    pixels[i + 1] = 0
    pixels[i + 2] = 0
    pixels[i + 3] = pixels[i + 3] > 0 ? 0 : 255
  }
  ctx.putImageData(imageData, 0, 0)
  return { canvas, ctx }
}

const getColored = ({ canvas, ctx }: CanvasBuffer, color: string) => {
  ctx.globalCompositeOperation = "source-in"
  ctx.fillStyle = color
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  return { canvas, ctx }
}

const getInnerShadow = ({ canvas, ctx }: CanvasBuffer, inverted: HTMLCanvasElement, image: HTMLCanvasElement | HTMLImageElement, imageOptions: IDrawImageOptions, { shadowBlur, shadowOffsetX, shadowOffsetY }: { shadowBlur: number, shadowOffsetX: number, shadowOffsetY: number }) => {
  ctx.shadowColor = "#000"
  ctx.shadowBlur = shadowBlur
  ctx.shadowOffsetX = shadowOffsetX
  ctx.shadowOffsetY = shadowOffsetY
  drawImage(ctx, inverted, imageOptions)
  ctx.globalCompositeOperation = "destination-in"
  drawImage(ctx, image, imageOptions)
  return { canvas, ctx }
}

export const getChain = (side: ISide) => ({
  render: () => render(side),
  setDirty: (dirty: boolean) => setDirty(dirty, side),
  setAsync: (async: boolean) => setAsync(async, side),
  setScale: (scale?: number) => setScale(scale, side),
  toImage: (type?: string, quantity?: number) => toImage(type, quantity, side),
  setPrintOnly: (printOnly: boolean) => setPrintOnly(printOnly, side),
  setShowPlaceholders: (show: boolean) => setShowPlaceholders(show, side),
  getOptions: () => getOptions(side),
})